import {
  ColumnHeightOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  VerticalAlignMiddleOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Form, Input, MenuProps, Space, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { ZAttrMeta } from "src/business/attribute";
import {
  AttrUserType,
  AttrUserTypeKey,
  getAttrUserType,
} from "src/business/attribute/AttrUserType";
import { attrUserTypes } from "src/business/attribute/AttrUserType/attrUserTypes";
import { debounce, DebounceCounter } from "src/common/debounce";
import { onError } from "src/common/onError";
import { CollapseButton } from "src/components/CollapseButton";
import { DeleteConfirm } from "src/components/DeleteConfirm";
import { HighlightedText } from "src/components/HighlightedText";

import { AttrEditorV2 } from "../AttrEditorV2";
import * as styles from "./AttrListV2.module.less";
import { AttrListV2Store } from "./AttrListV2Store";
import { AttrRow } from "./AttrRow";

const name = ["definition", "attributes"];

type PropsAttrListV2 = {
  value?: unknown;
  onChange?(newValue: unknown): void;
  availableAttrTypes?: Set<AttrUserTypeKey>;
};

export const AttrListV2: React.FC<PropsAttrListV2> = observer((props) => {
  const { value, onChange, availableAttrTypes } = props;
  const store = React.useMemo(() => new AttrListV2Store(), []);
  React.useEffect(() => {
    store.setAttrListRaw(value);
  }, [value]);

  const columns: ColumnsType<AttrRow> = [
    {
      key: "displayId",
      title: "Display ID",
      width: "100px",
      render: (_, row) => (
        <HighlightedText
          text={String(row.attr.displayId)}
          selected={store.search}
        />
      ),
    },
    {
      key: "name",
      title: "Системное название",
      render: (_, row) => <NameBox store={store} row={row} />,
    },
    {
      key: "title",
      title: "Отображаемое название",
      render: (_, row) => (
        <HighlightedText text={row.attr.title} selected={store.search} />
      ),
    },
    {
      key: "type",
      title: "Тип атрибута",
      render: (_, { attr }) => <AttrType attr={attr} selected={store.search} />,
    },
    {
      key: "order",
      title: "Ранг",
      render: (_, { attr }) => attr?.edit?.order,
      width: 50,
      align: "center",
    },
    {
      key: "@addSubAttr",
      title: "",
      width: 48,
      render: (_, row) =>
        row.attr.type.name === "list" && (
          <AddAttr action={(t) => store.onNewAttr(t, row)}>
            <Button
              icon={<PlusCircleOutlined />}
              title="Добавить вложенный атрибут"
            />
          </AddAttr>
        ),
    },
    {
      key: "@edit",
      title: "",
      width: 48,
      render: (_, row) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          title="Редактировать"
          onClick={() => store.onEditAttr(row)}
        />
      ),
    },
  ];

  const [curSearch, setCurSearch] = React.useState("");
  React.useEffect(() => {
    setCurSearch(store.search);
  }, [store.search]);
  const onSearch: React.ChangeEventHandler<HTMLInputElement> = ({
    currentTarget: { value: newSearch },
  }) => {
    setCurSearch(newSearch);
    debounce(searchCounter, 200, () => store.setSearch(newSearch));
  };

  return (
    <div className={styles.attributes}>
      <div className={styles.attrHeader}>Атрибуты</div>
      <div className={styles.toolbar}>
        <Input
          prefix={<SearchOutlined />}
          className={styles.search}
          placeholder="Найти атрибут"
          value={curSearch}
          onChange={onSearch}
          allowClear
        />
        {store.allCollapsibleKeys.length > 0 && (
          <>
            <Button
              title="Раскрыть всё"
              onClick={() => store.expandAll()}
              icon={<ColumnHeightOutlined />}
              disabled={!store.canExpandAll}
            />
            <Button
              title="Свернуть всё"
              onClick={() => store.collapseAll()}
              icon={<VerticalAlignMiddleOutlined />}
              disabled={!store.canCollapseAll}
            />
          </>
        )}
        <Button
          icon={<DeleteOutlined />}
          disabled={!store.canDelete}
          title={store.deleteMsg}
          danger
          onClick={() => store.setDeleteMode(true)}
        >
          Удалить
        </Button>
        <AddAttr
          action={(t) => store.onNewAttr(t)}
          availableAttrTypes={availableAttrTypes}
        >
          <Button>Добавить атрибут</Button>
        </AddAttr>
      </div>
      <Form.Item name={name}>
        <Table<AttrRow>
          dataSource={store.visibleRows}
          rowKey="key"
          columns={columns}
          pagination={false}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: store.selectedRowKeys,
            getCheckboxProps: ({ key }) => ({
              disabled: store.selectedOwners.has(key),
            }),
            onSelect: ({ key }) => store.toggleSelection(key),
            onChange: (_keys, _rows, { type }) => {
              if (type === "all") store.toggleSelectionAll();
            },
          }}
          locale={{
            emptyText: store.emptyText,
          }}
        />
      </Form.Item>
      <DeleteConfirm
        content="Удалить выделенное?"
        open={store.deleteMode}
        close={() => store.setDeleteMode(false)}
        task={async () => {
          const newValue = store.doDelete();
          onChange?.(newValue);
        }}
      />
      <AttrEditorV2
        store={store.attrStore}
        close={() => store.setAttrStore(null)}
        onOk={(newAttr) => {
          try {
            onChange?.(store.updateAttr(newAttr));
          } catch (e) {
            onError(e);
          }
        }}
      />
    </div>
  );
});

type PropsAddAttr = {
  children: React.ReactNode;
  action: (userType: AttrUserType) => void;
  availableAttrTypes?: Set<AttrUserTypeKey>;
};

export const AddAttr: React.FC<PropsAddAttr> = (props) => {
  const { children, action, availableAttrTypes } = props;
  const types: AttrUserType[] = availableAttrTypes
    ? attrUserTypes.filter(({ key }) => availableAttrTypes.has(key))
    : [...attrUserTypes];
  types.sort((a, b) => a.name.localeCompare(b.name));
  const items: MenuProps["items"] = types.map((it) => ({
    key: it.key,
    icon: it.icon,
    label: it.name,
    onClick: () => action(it),
  }));
  return (
    <Dropdown menu={{ items }} trigger={["click"]}>
      {children}
    </Dropdown>
  );
};

type PropsAttrType = {
  attr: ZAttrMeta;
  selected: string;
};

const AttrType: React.FC<PropsAttrType> = ({ attr, selected }) => {
  const t = getAttrUserType(attr);
  if (!t) return null;
  return (
    <Space>
      {t.icon}
      <HighlightedText text={t.name} selected={selected} />
    </Space>
  );
};

type PropsNameBox = {
  row: AttrRow;
  store: AttrListV2Store;
};

const NameBox: React.FC<PropsNameBox> = observer((props) => {
  const { row, store } = props;
  return (
    <div
      className={styles.nameBox}
      style={{ paddingLeft: 24 * row.ownerKeys.length }}
    >
      <div className={styles.collapse}>
        {!row.isLeaf && (
          <CollapseButton
            collapsed={store.collapsed.has(row.key)}
            onChange={() => {
              store.toggleCollapse(row.key);
            }}
          />
        )}
      </div>
      <div className={styles.nameItem}>
        <HighlightedText text={row.attr.name} selected={store.search} />
      </div>
    </div>
  );
});

const searchCounter: DebounceCounter = {};
